import { useEffect, useState, useRef } from "react";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { createClient } from '@/lib/supabase/client';
import { DataTable, DataTableColumn } from '@/components/ui/data-table';
import { CrudForm, CrudFormField } from '@/components/forms/crud-form';
import { ChangeEvent } from "react";
import { useNavigate } from 'react-router-dom';

interface CourseForm {
  title: string;
  description: string;
  status: string;
  image_url: string;
}

const courseFields: CrudFormField[] = [
  { name: 'title', label: 'Nome do Curso', type: 'text', required: true },
  { name: 'description', label: 'Descrição', type: 'textarea', required: true },
  { name: 'status', label: 'Status', type: 'select', required: true, options: [
    { value: 'active', label: 'Ativo' },
    { value: 'inactive', label: 'Inativo' },
  ] },
];

function ImageUpload({ value, onChange }: { value: string | File | null, onChange: (file: File | null) => void }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      onChange(e.target.files[0]);
    }
  };
  return (
    <div className="flex flex-col items-center gap-2 mb-4">
      <div
        className="w-32 h-32 rounded-lg border flex items-center justify-center bg-muted cursor-pointer overflow-hidden"
        onClick={() => fileInputRef.current?.click()}
        style={{ position: "relative" }}
      >
        {value ? (
          typeof value === "string" ? (
            <img src={value} alt="Preview" className="object-cover w-full h-full" />
          ) : (
            <img src={URL.createObjectURL(value)} alt="Preview" className="object-cover w-full h-full" />
          )
        ) : (
          <span className="text-muted-foreground">Selecionar imagem</span>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      <button
        type="button"
        className="text-xs text-red-500"
        onClick={() => onChange(null)}
        style={{ display: value ? "block" : "none" }}
      >
        Remover imagem
      </button>
    </div>
  );
}

export default function CursosPage() {
  const [cursos, setCursos] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<CourseForm>({
    title: '',
    description: '',
    status: 'active',
    image_url: '',
  });
  const [editId, setEditId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const navigate = useNavigate();
  const supabase = createClient();

  useEffect(() => {
    const fetchCursos = async () => {
      setLoading(true);
      let query = supabase.from('courses').select('*');
      if (search) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
      }
      const { data } = await query;
      setCursos(data || []);
      setLoading(false);
    };
    fetchCursos();
  }, [search]);

  const handleImagesChange = (files: FileList) => {
    if (files && files.length > 0) {
      setSelectedImage(files[0]);
    } else {
      setSelectedImage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    let imageUrl = form.image_url;
    if (selectedImage) {
      // Upload da imagem para o bucket 'course-images'
      const fileExt = selectedImage.name.split('.').pop();
      const filePath = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const { error: uploadError } = await supabase.storage.from('course-images').upload(filePath, selectedImage, { upsert: false });
      if (!uploadError) {
        const { data: urlData } = supabase.storage.from('course-images').getPublicUrl(filePath);
        imageUrl = urlData.publicUrl;
      } else {
        alert('Erro ao fazer upload da imagem: ' + uploadError.message);
        setLoading(false);
        return;
      }
    }
    const payload = {
      title: form.title,
      description: form.description,
      status: form.status,
      image_url: imageUrl,
    };
    if (editId) {
      await supabase.from('courses').update(payload).eq('id', editId);
      setOpen(false);
      setForm({ title: '', description: '', status: 'active', image_url: '' });
      setSelectedImage(null);
      setEditId(null);
      setLoading(false);
      // Recarregar lista
      let query = supabase.from('courses').select('*');
      if (search) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
      }
      const { data } = await query;
      setCursos(data || []);
    } else {
      // Criação
      const { data, error } = await supabase.from('courses').insert(payload).select().single();
      setOpen(false);
      setForm({ title: '', description: '', status: 'active', image_url: '' });
      setSelectedImage(null);
      setEditId(null);
      setLoading(false);
      // Recarregar lista
      let query = supabase.from('courses').select('*');
      if (search) {
        query = query.or(`title.ilike.%${search}%,description.ilike.%${search}%`);
      }
      const { data: cursosData } = await query;
      setCursos(cursosData || []);
      // Redirecionar para a página de edição do curso recém-criado
      if (data && data.id) {
        navigate(`/dashboard/admin/cursos/${data.id}`);
      }
    }
  };

  const handleEdit = (curso: any) => {
    setForm({
      title: curso.title || '',
      description: curso.description || '',
      status: curso.status || 'active',
      image_url: curso.image_url || '',
    });
    setSelectedImage(null);
    setEditId(curso.id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm({ title: '', description: '', status: 'active', image_url: '' });
    setSelectedImage(null);
  };

  const handleDelete = async (cursoId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este curso?')) return;
    setLoading(true);
    await supabase.from('courses').delete().eq('id', cursoId);
    setCursos((prev) => prev.filter((c) => c.id !== cursoId));
    setLoading(false);
  };

  const cursoColumns: DataTableColumn<any>[] = [
    { key: 'title', label: 'Nome' },
    { key: 'description', label: 'Descrição' },
    { key: 'status', label: 'Status', render: c => c.status === 'active' ? 'Ativo' : 'Inativo' },
    { key: 'image_url', label: 'Imagem', render: c => c.image_url ? <img src={c.image_url} alt="Capa" className="h-8 w-8 object-cover rounded" /> : '-' },
    {
      key: 'actions',
      label: 'Ações',
      render: c => (
        <>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(c)}>Editar</Button>
          <Button variant="destructive" size="sm" onClick={() => handleDelete(c.id)} className="ml-2">Excluir</Button>
        </>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Cursos</h2>
          <p className="text-muted-foreground">
            Gerencie os cursos da plataforma
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Buscar cursos..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-56"
          />
          <Dialog open={open} onOpenChange={(v) => v ? setOpen(true) : handleClose()}>
            <DialogTrigger asChild>
              <Button className="w-full md:w-auto" onClick={() => { setEditId(null); setForm({ title: '', description: '', status: 'active', image_url: '' }); setSelectedImage(null); }}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Curso
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editId ? 'Editar Curso' : 'Novo Curso'}</DialogTitle>
              </DialogHeader>
              {/* Campo customizado de upload de imagem com preview */}
              <ImageUpload
                value={selectedImage ? selectedImage : form.image_url}
                onChange={(file) => setSelectedImage(file)}
              />
              <CrudForm
                fields={courseFields}
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                onCancel={handleClose}
                submitLabel="Salvar"
                cancelLabel="Cancelar"
                loading={loading}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="rounded-md border">
        <DataTable
          columns={cursoColumns}
          data={cursos}
          loading={loading}
        />
      </div>
    </div>
  );
} 